import { useEffect, useState } from "react";

import { Download } from "@carbon/icons-react";

import Demo from "@/assets/images/Demo.png";
import { Button } from "@/components/ui/button";

import Footer from "./components/Footer";
import Header from "./components/Header";

const detectOS = () => {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  if (/Android/.test(userAgent)) return "Android";
  if (/iPhone|iPad|iPod/.test(userAgent)) return "iOS";
  if (platform.startsWith("Win")) return "Windows";
  if (platform.startsWith("Mac")) return "MacOS";
  if (platform.startsWith("Linux") || userAgent.indexOf("Ubuntu") !== -1)
    return "Linux";

  return "Unknown";
};

const DownloadPage = () => {
  const [link, setLink] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const os = detectOS();

  const determineLink = (osName: string) => {
    switch (osName) {
      case "Windows":
        return "#*";
      case "MacOS":
        return "#*";
      case "Linux":
        return "#*";
      case "Android":
        return "https://drive.google.com/file/d/1i2cWWGcW8UCidyOGTFGsxPpJS-q5M1pZ/view";
      case "iOS":
        return "#*";
      default:
        return "#*";
    }
  };

  const handleDownload = async () => {
    setIsLoading(true);
    // Simulate download delay
    setTimeout(() => {
      setIsLoading(false);
      window.open(link, "_blank");
    }, 1000);
  };

  useEffect(() => {
    setLink(determineLink(os));
  }, [os]);

  const platforms = [
    { name: "MacOS", icon: "🍎", available: false },
    { name: "Windows", icon: "🪟", available: false },
    { name: "Linux", icon: "🐧", available: false },
  ];

  return (
    <>
      <Header />

      {/* Hero Section with Modern Design */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900 overflow-hidden">
        {/* Background Decorations */}

        <div className="relative max-w-7xl mx-auto px-4 pt-20 pb-16 min-h-[80vh] flex items-center">
          <div className="w-full text-center">
            {/* Main Heading with Modern Typography */}
            <h1 className="max-w-4xl mx-auto mb-6 text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight">
              <span className="bg-gradient-to-r from-purple-600 via-blue-600 to-green-600 text-transparent bg-clip-text animate-gradient-x">
                Tải xuống TagaNow
              </span>
            </h1>

            {/* Description */}
            <p className="max-w-2xl mx-auto mb-12 text-lg md:text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
              Trải nghiệm TagaNow với hiệu suất tối ưu và bảo mật cao trên thiết
              bị của bạn
            </p>

            {/* Download Button */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              {os === "Android" ? (
                <Button
                  onClick={handleDownload}
                  disabled={isLoading}
                  className="group relative px-8 py-4 h-15 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-lg  hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed min-w-[280px]"
                >
                  <div className="flex items-center justify-center">
                    {isLoading ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                    ) : (
                      <Download
                        className="mr-3 group-hover:scale-110 transition-transform duration-300"
                        size={24}
                      />
                    )}
                    {isLoading ? "Đang tải xuống..." : "Tải xuống cho Android"}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur opacity-30 group-hover:opacity-50 transition-opacity duration-300 -z-10"></div>
                </Button>
              ) : (
                <div className="text-center">
                  <Button
                    className="px-8 py-4 text-lg bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400  cursor-not-allowed min-w-[280px]"
                    disabled
                  >
                    Chưa hỗ trợ cho {os !== "Unknown" ? os : "Hệ điều hành"}
                  </Button>
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    Sẽ sớm có sẵn cho nền tảng này
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section with Modern Cards */}
      <section className="relative bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-3xl   overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 lg:p-12">
              {/* Image Section */}
              <div className="flex items-center justify-center order-2 lg:order-1">
                <div className="relative">
                  <img
                    src={Demo}
                    alt="TagaNow Demo"
                    className="relative  h-auto transform hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>

              {/* Content Section */}
              <div className="order-1 lg:order-2 flex flex-col justify-center">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-200 mb-4 leading-tight">
                  Trải nghiệm TagaNow <br />
                  <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-transparent bg-clip-text">
                    trên di động
                  </span>
                </h2>

                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                  Tận hưởng tính năng đồng bộ hóa an toàn và truy cập máy chủ
                  của bạn mọi lúc mọi nơi với ứng dụng di động TagaNow.
                </p>

                {/* Store Badges */}
                <div className="flex flex-wrap gap-4">
                  <a
                    href={determineLink("iOS")}
                    className="transform hover:scale-105 transition-transform duration-300 filter hover:brightness-110"
                  >
                    <img
                      src="https://framerusercontent.com/images/1ALkV1Eckh03AD1MTmcoyxlGPw.png"
                      alt="Download on App Store"
                      className="h-14 w-auto rounded-lg shadow-lg"
                    />
                  </a>
                  <a
                    href={determineLink("Android")}
                    className="transform hover:scale-105 transition-transform duration-300 filter hover:brightness-110"
                  >
                    <img
                      src="https://framerusercontent.com/images/Ech0madLMiImF0Zw2dNeN7AWU.png"
                      alt="Get it on Google Play"
                      className="h-14 w-auto rounded-lg shadow-lg"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Desktop Platforms Section */}
      <section className="bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-800 dark:via-gray-900 dark:to-purple-900 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 dark:text-gray-200 mb-4">
              Tải xuống cho
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-transparent bg-clip-text">
                {" "}
                Desktop
              </span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Chọn phiên bản phù hợp với hệ điều hành của bạn
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {platforms.map((platform, index) => (
              <div
                key={index}
                className="group relative bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                {!platform.available && (
                  <div className="absolute top-4 right-4 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-xs px-2 py-1 rounded-full font-medium">
                    Sắp ra mắt
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-6">
                    {platform.name}
                  </h3>

                  <Button
                    variant="outline"
                    disabled={!platform.available}
                    className={`w-full py-3 text-lg font-medium rounded-xl transition-all duration-300 ${
                      platform.available
                        ? "border-purple-300 text-purple-600 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:hover:bg-purple-900/20"
                        : "opacity-50 cursor-not-allowed"
                    }`}
                  >
                    <Download
                      className="mr-2"
                      size={20}
                    />
                    {platform.available ? "Tải xuống" : "Chưa có sẵn"}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Coming Soon Notice */}
          <div className="text-center mt-12">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-full">
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300 mr-2">
                🚀 Các phiên bản desktop sẽ sớm ra mắt
              </span>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes gradient-x {
            0%, 100% {
              background-size: 200% 200%;
              background-position: left center;
            }
            50% {
              background-size: 200% 200%;
              background-position: right center;
            }
          }
          .animate-gradient-x {
            animation: gradient-x 3s ease infinite;
          }
        `,
        }}
      />
    </>
  );
};

export default DownloadPage;
